import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Home, Mic, Users, Settings, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useStoryStore } from '../../store/storyStore';
import { useNavigation } from '../../contexts/NavigationContext';

interface DesktopSidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onOpenStoryPicker: () => void;
  onOpenFamilyManagement: () => void;
}

const DesktopSidebar: React.FC<DesktopSidebarProps> = ({
  isCollapsed,
  onToggleCollapse,
  onOpenStoryPicker,
  onOpenFamilyManagement
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin } = useAuthStore();
  const { stories } = useStoryStore();
  const { lastActiveStoryId } = useNavigation();

  const isOnDashboard = location.pathname === '/dashboard';
  const isOnStoryRecorder = location.pathname.startsWith('/story-recorder');
  const isOnAdmin = location.pathname === '/admin';

  const getIncompleteStories = () => {
    return stories.filter(s => s.progress < 100);
  };

  const handleRecordClick = () => {
    const incompleteStories = getIncompleteStories();

    if (isOnStoryRecorder) {
      const storyId = location.pathname.split('/story-recorder/')[1];
      navigate(`/story-recorder/${storyId}?openOverlay=true`);
      return;
    }

    if (incompleteStories.length === 0) {
      onOpenStoryPicker();
      return;
    }

    if (incompleteStories.length === 1) {
      navigate(`/story-recorder/${incompleteStories[0].id}?openOverlay=true`);
      return;
    }

    if (lastActiveStoryId) {
      const lastStory = incompleteStories.find(s => s.id === lastActiveStoryId);
      if (lastStory && lastStory.progress < 100) {
        navigate(`/story-recorder/${lastActiveStoryId}?openOverlay=true`);
        return;
      }
    }

    onOpenStoryPicker();
  };

  const navItems = [
    {
      id: 'home',
      icon: Home,
      label: 'Historias',
      isActive: isOnDashboard || isOnStoryRecorder,
      onClick: () => navigate('/dashboard')
    },
    {
      id: 'record',
      icon: Mic,
      label: 'Grabar',
      isActive: false,
      onClick: handleRecordClick,
      isPrimary: true
    },
    {
      id: 'family',
      icon: Users,
      label: 'Familias',
      isActive: false,
      onClick: onOpenFamilyManagement
    }
  ];

  return (
    <aside
      className={`hidden lg:flex flex-col bg-white border-r border-stone-200 transition-all duration-300 fixed top-16 left-0 bottom-0 z-sticky ${
        isCollapsed ? 'w-16' : 'w-60'
      }`}
    >
      <nav className="flex-1 py-4">
        <ul className="space-y-1 px-2">
          {navItems.map((item) => {
            const Icon = item.icon;

            if (item.isPrimary) {
              return (
                <li key={item.id} className="py-2">
                  <button
                    onClick={item.onClick}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl bg-gradient-to-r from-rose-400 to-rose-500 text-white hover:from-rose-500 hover:to-rose-600 shadow-md transition-all ${
                      isCollapsed ? 'justify-center' : ''
                    }`}
                  >
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    {!isCollapsed && (
                      <span className="font-medium">{item.label}</span>
                    )}
                  </button>
                </li>
              );
            }

            return (
              <li key={item.id}>
                <button
                  onClick={item.onClick}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    item.isActive
                      ? 'bg-rose-50 text-rose-700'
                      : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900'
                  } ${isCollapsed ? 'justify-center' : ''}`}
                >
                  <Icon className={`h-5 w-5 flex-shrink-0 ${item.isActive ? 'stroke-[2.5px]' : ''}`} />
                  {!isCollapsed && (
                    <span className={item.isActive ? 'font-medium' : ''}>
                      {item.label}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {isAdmin && (
        <div className="px-2 pb-2">
          <Link
            to="/admin"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              isOnAdmin
                ? 'bg-stone-100 text-stone-900'
                : 'text-stone-500 hover:bg-stone-50 hover:text-stone-700'
            } ${isCollapsed ? 'justify-center' : ''}`}
          >
            <Settings className="h-5 w-5 flex-shrink-0" />
            {!isCollapsed && <span>Administración</span>}
          </Link>
        </div>
      )}

      <div className="border-t border-stone-200 p-2">
        <button
          onClick={onToggleCollapse}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-stone-500 hover:bg-stone-50 rounded-lg transition-colors"
        >
          {isCollapsed ? (
            <ChevronRight className="h-5 w-5" />
          ) : (
            <>
              <ChevronLeft className="h-5 w-5" />
              <span className="text-sm">Colapsar</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
};

export default DesktopSidebar;
